import { useEffect, useState, useCallback, useRef, RefObject } from 'react';

const useScrollVisibility = (
  containerRef: RefObject<HTMLElement>,
  location?: string // Add location parameter
) => {
  const [visibleElements, setVisibleElements] = useState<Set<string>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [visibilityEventType, setVisibilityEventType] = useState<'show' | 'scroll' | null>(null);
  const direction = useRef<string | null>(null);
  const rafId = useRef<number>();
  const dragRafId = useRef<number>();
  const hasInitialCheckRun = useRef<boolean>(false);
  const previousLocation = useRef<string | undefined>();

  const checkVisibility = useCallback(
    (eventType: 'show' | 'scroll' = 'scroll') => {
      const container = containerRef.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const chips = container.querySelectorAll('[data-visibility-id]');
      const currentDirection = container.querySelectorAll('[data-scroll-direction]');
      direction.current = currentDirection[0]?.getAttribute('data-scroll-direction') || null;

      const visible = new Set<string>();

      chips.forEach((chip) => {
        const chipRect = chip.getBoundingClientRect();
        const id = chip.getAttribute('data-visibility-id');

        if (!id) return;

        // Check if chip is visible in viewport (not just container)
        const isVisible =
          chipRect.right > 0 &&
          chipRect.left < window.innerWidth &&
          chipRect.bottom > 0 &&
          chipRect.top < window.innerHeight &&
          // Also check if it's within the scrollable container
          chipRect.right > containerRect.left &&
          chipRect.left < containerRect.right &&
          chipRect.bottom > containerRect.top &&
          chipRect.top < containerRect.bottom;

        if (isVisible) {
          visible.add(id);
        }
      });

      setVisibleElements(visible);

      // Only set the event type if there are visible elements or if it's the initial check
      if (visible.size > 0 || eventType === 'show') {
        setVisibilityEventType(eventType);
      }
    },
    [containerRef]
  );

  // Initial visibility check
  const initialVisibilityCheck = useCallback(() => {
    if (!hasInitialCheckRun.current || previousLocation.current !== location) {
      checkVisibility('show');
      hasInitialCheckRun.current = true;
      previousLocation.current = location;
    }
  }, [checkVisibility, location]);

  // Reset initial check when location changes
  useEffect(() => {
    if (previousLocation.current && previousLocation.current !== location) {
      hasInitialCheckRun.current = false;
      // Trigger initial check after a small delay to ensure DOM updates
      setTimeout(() => {
        initialVisibilityCheck();
      }, 50);
    }
  }, [location, initialVisibilityCheck]);

  // Scrolling visibility check
  const scrollVisibilityCheck = useCallback(() => {
    checkVisibility('scroll');
  }, [checkVisibility]);

  // Throttled version using requestAnimationFrame for scroll events
  const throttledCheckVisibility = useCallback(() => {
    if (rafId.current) {
      cancelAnimationFrame(rafId.current);
    }

    rafId.current = requestAnimationFrame(() => {
      scrollVisibilityCheck();
    });
  }, [scrollVisibilityCheck]);

  // High-frequency visibility check for dragging (using requestAnimationFrame loop)
  const continuousVisibilityCheck = useCallback(() => {
    if (isDragging) {
      scrollVisibilityCheck(); // Dragging counts as scroll
      dragRafId.current = requestAnimationFrame(continuousVisibilityCheck);
    }
  }, [isDragging, scrollVisibilityCheck]);

  // Start continuous checking when dragging starts
  useEffect(() => {
    if (isDragging) {
      continuousVisibilityCheck();
    } else if (dragRafId.current) {
      cancelAnimationFrame(dragRafId.current);
    }

    return () => {
      if (dragRafId.current) {
        cancelAnimationFrame(dragRafId.current);
      }
    };
  }, [isDragging, continuousVisibilityCheck]);

  // Detect dragging events on the container
  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    // Do a final visibility check after dragging ends
    setTimeout(() => scrollVisibilityCheck(), 50);
  }, [scrollVisibilityCheck]);

  // Handle mouse/touch events for detecting dragging
  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setTimeout(() => scrollVisibilityCheck(), 50);
  }, [scrollVisibilityCheck]);

  const handleTouchStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    setTimeout(() => scrollVisibilityCheck(), 50);
  }, [scrollVisibilityCheck]);

  // Handle pointer events (modern approach that works for both mouse and touch)
  const handlePointerDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
    setTimeout(() => scrollVisibilityCheck(), 50);
  }, [scrollVisibilityCheck]);

  // Listen for drag events from DraggableScroll component
  const handleDraggableScrollEvent = useCallback(
    (e: CustomEvent) => {
      switch (e.detail.type) {
        case 'dragStart':
          setIsDragging(true);
          break;
        case 'dragEnd':
          setIsDragging(false);
          setTimeout(() => scrollVisibilityCheck(), 50);
          break;
        case 'dragging':
          // Visibility is already being checked continuously
          break;
        default:
          break;
      }
    },
    [scrollVisibilityCheck]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = throttledCheckVisibility;
    const handleResize = throttledCheckVisibility;

    // Standard scroll and resize listeners
    container.addEventListener('scroll', handleScroll);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    // Drag detection listeners
    container.addEventListener('dragstart', handleDragStart);
    container.addEventListener('dragend', handleDragEnd);

    // Mouse events for drag detection
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mouseup', handleMouseUp);

    // Touch events for mobile dragging
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    // Pointer events (preferred modern approach)
    container.addEventListener('pointerdown', handlePointerDown);
    container.addEventListener('pointerup', handlePointerUp);

    // Custom events from DraggableScroll component
    container.addEventListener('draggableScrollEvent', handleDraggableScrollEvent);

    // Look for DraggableScroll wrapper and listen to its events
    const draggableScrollWrapper = container.querySelector('.draggable-scroll-wrapper');
    if (draggableScrollWrapper) {
      draggableScrollWrapper.addEventListener('draggableScrollEvent', handleDraggableScrollEvent);
    }

    // Initial check after a small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      initialVisibilityCheck();
    }, 100);

    return () => {
      // Remove all event listeners
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);

      container.removeEventListener('dragstart', handleDragStart);
      container.removeEventListener('dragend', handleDragEnd);

      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mouseup', handleMouseUp);

      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);

      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('pointerup', handlePointerUp);

      container.removeEventListener('draggableScrollEvent', handleDraggableScrollEvent);

      if (draggableScrollWrapper) {
        draggableScrollWrapper.removeEventListener('draggableScrollEvent', handleDraggableScrollEvent);
      }

      // Cancel any pending animation frames
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
      if (dragRafId.current) {
        cancelAnimationFrame(dragRafId.current);
      }

      clearTimeout(timeoutId);
    };
  }, [
    containerRef,
    throttledCheckVisibility,
    initialVisibilityCheck,
    handleDragStart,
    handleDragEnd,
    handleMouseDown,
    handleMouseUp,
    handleTouchStart,
    handleTouchEnd,
    handlePointerDown,
    handlePointerUp,
    handleDraggableScrollEvent,
  ]);

  return {
    visibleElements,
    isDragging,
    visibilityEventType,
    scrollDirection: direction.current,
  };
};

export default useScrollVisibility;
